import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderHeaderComponent,
  ElderPaneContentComponent,
  ElderSinglePaneWrapperComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { BatchTracingService } from '../../batch-tracing.service'
import { TracedMaterialUpdate } from '../../traced-material-update'
import { TracedMaterial } from '../traced-material'
import { TracedMaterialGroupedComponent } from '../traced-material-grouped/traced-material-grouped.component'
import { TracedMaterialSingleComponent } from '../traced-material-single/traced-material-single.component'

@Component({
  selector: 'app-batch-tracing-conf-browser',
  templateUrl: './traced-material-browser.component.html',
  styleUrls: ['./traced-material-browser.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    TracedMaterialSingleComponent,
    TracedMaterialGroupedComponent,
    ElderHeaderComponent,
    MatSlideToggle,
    FormsModule,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class TracedMaterialBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _tracedMaterials$ = new BehaviorSubject<TracedMaterial[]>([])

  private readonly _groupTracedMaterials$ = new BehaviorSubject<boolean>(true)

  private readonly batchTracingService = inject(BatchTracingService)

  private toastService = inject(ElderToastService)

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set tracedMaterials(materials: TracedMaterial[]) {
    this._tracedMaterials$.next(materials)
  }

  public get tracedMaterials$(): Observable<TracedMaterial[]> {
    return this._tracedMaterials$.asObservable()
  }

  public get groupTracedMaterials(): boolean {
    return this._groupTracedMaterials$.getValue()
  }

  public get groupTracedMaterials$(): Observable<boolean> {
    return this._groupTracedMaterials$.asObservable()
  }

  public set groupTracedMaterials(value: boolean) {
    this._groupTracedMaterials$.next(value)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onTracingConfigUpdates(updates: TracedMaterialUpdate[]): void {
    this.batchTracingService.saveAll(updates).subscribe({
      next: (_) => {
        this.toastService.info('messages.persisting_successful')
        this.emitClose()
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }
}
